import React, { createContext, useContext, useState } from 'react'
import { useAuthContext } from '../AuthContext/AuthContext'
import {
  fetchActiveQuestions,
  fetchQuestionsGroup,
  fetchSingleQuestion,
  Question,
  submitAnswer,
  QuestionParams,
  DEFAULT_EXTRA_LOGIN_FIELDS,
} from '../../Api'
import { isQuestionChangable, sortQuestions } from '../../Utils'
import { CURRENT_YEAR } from '../../Config/League/league.config'
import { SnackbarContext } from '../SnackbarContext/SnackbarContext'

type QuestionContextType = {
  questionIndex: number
  questions: Question[]
  error: QuestionError
  getQuestionSet: (params: QuestionParams) => Promise<Question[]>
  getActiveQuestions: (year: number) => Promise<Question[]>
  handleSubmitAnswer: (question: Question, answerCode: string) => Promise<boolean>
  goToNext: (questionIndex: number) => void
  goBack: (questionIndex: number) => void
  goToQuestion: (qIndex: number) => void
}
type QuestionProviderProps = { eventId?: string }
export type QuestionError = {
  [key: string]: {
    questionId: string
    display: boolean
    message: string
  }
} | null

export const QuestionContext = createContext<QuestionContextType | null>(null)

export const QuestionProvider: React.FC<
  React.PropsWithChildren<QuestionProviderProps>
> = ({ children }) => {
  const [questionIndex, setQuestionIndex] = useState<number>(0)
  const [questions, setQuestions] = useState<Question[]>([])
  const [error, setError] = useState<QuestionError>(null)
  const { isLoggedIn, login, userId } = useAuthContext()
  const { toggleShowSnackbar } = useContext(SnackbarContext)!

  const handleSubmitAnswer = async (question: Question, answerCode: string) => {
    if (!isLoggedIn && !userId && question.authRequired) {
      console.log('User is anonymous & auth is required')
      sessionStorage.setItem(
        'login_text',
        'Log in to submit and save your answers to questions'
      )
      login(DEFAULT_EXTRA_LOGIN_FIELDS)
      return false
    }

    const currentTime = Math.trunc(Date.now() / 1000)
    if (question.endTime && currentTime > question.endTime) {
      console.log('The question is closed.')
      updateErrorObj(question.questionId, 'The question is closed.')
      return false
    }

    if (question.userAnswerCode) {
      const canChange = isQuestionChangable(question)
      if (!canChange) {
        console.log('You cannot change your answer.')
        updateErrorObj(question.questionId, 'You cannot change your answer.')
        return false
      }
    }

    if (question.appExclusive) {
      console.log('App exclusive: Download to participate.')
      updateErrorObj(question.questionId, 'App exclusive: Download to participate.')
      return false
    }

    if (userId) {
      const qRes = await submitAnswer(question.id, userId, answerCode)
      if (qRes?.error) {
        toggleShowSnackbar({
          text: qRes.error,
          color: 'red',
        })
        updateErrorObj(question.questionId, qRes.error)
        return false
      }
      if (qRes?.question) {
        updateAnswerInState(qRes.question)
        return true
      }
    }
    console.log('Unknown error on question submit')
    updateErrorObj(question.questionId, 'Something went wrong.')
    return false
  }

  /**
   * Determine what type of question function to call based on type
   */
  const getQuestionSet = async (params: QuestionParams): Promise<Question[]> => {
    let questions: Question[] = []

    switch (params.qType) {
      case 'group':
        if (params.group) {
          const groupQs = await getGroupQuestions(params.group)
          const qsToSet = groupQs ? sortQuestions(groupQs) : questions
          getFirstUnansweredQuestion(qsToSet)
          setQuestions(qsToSet)

          return qsToSet
        }
        return questions
      case 'allActive':
        const activeQs = await getActiveQuestions(params.year || CURRENT_YEAR)
        setQuestions(activeQs || questions)
        return activeQs || questions
      case 'single':
        if (params.questionId) {
          const q = await getQuestionById(params.questionId)
          setQuestions(q ? [q] : questions)
          return q ? [q] : questions
        }
        return questions
      default:
        return questions
    }
  }

  const getActiveQuestions = async (year: number): Promise<Question[]> => {
    try {
      const res = await fetchActiveQuestions(year, userId)
      if (res.error) {
        toggleShowSnackbar({
          text: res.error,
          color: 'red',
        })
        return []
      }

      return res.questions
    } catch (err) {
      console.log(err)
      return []
    }
  }

  const getGroupQuestions = async (group: string): Promise<Question[]> => {
    try {
      const res = await fetchQuestionsGroup(group, userId)
      if (res.error) {
        toggleShowSnackbar({
          text: res.error,
          color: 'red',
        })
        return []
      }
      return res.questions
    } catch (err) {
      toggleShowSnackbar({
        text: 'Error fetching group questions',
        color: 'red',
      })
      return []
    }
  }

  const getQuestionById = async (questionId: string): Promise<Question | null> => {
    try {
      const res = await fetchSingleQuestion(questionId, userId)
      if (res.error) {
        updateErrorObj(questionId, res.error)
        return null
      }
      return res.question
    } catch (err) {
      console.log(err)
      return null
    }
  }

  const goToQuestion = (qIndex: number) => {
    setQuestionIndex(qIndex)
  }

  const goToNext = (questionIndex: number) => {
    setQuestionIndex(questionIndex + 1)
  }

  const goBack = (questionIndex: number) => {
    setQuestionIndex(questionIndex - 1)
  }

  const updateAnswerInState = (question: Question): boolean => {
    let qs = [...questions]
    const idx = qs.findIndex((q) => q.questionId === question.questionId)
    if (idx !== -1) {
      qs[idx] = question
      setQuestions(qs)
      return true
    }
    return false
  }

  const updateErrorObj = async (questionId: string, message: string) => {
    let errObjs = { ...error }
    const qId = questionId as keyof QuestionError
    errObjs[qId] = {
      questionId: questionId,
      display: true,
      message: message,
    }
    setError(errObjs)
    setTimeout(() => {
      setError(null)
    }, 4000)
  }

  const getFirstUnansweredQuestion = (questions: Question[]) => {
    if (questions.length < 1) {
      setQuestionIndex(0)
      return
    }
    let firstUnanswered = questions.findIndex((q) => !q.userAnswerCode)
    if (firstUnanswered === -1) {
      setQuestionIndex(questions.length) //Summary shows if all questions answered
      return
    }
    setQuestionIndex(firstUnanswered)
  }

  return (
    <QuestionContext.Provider
      value={{
        questionIndex,
        questions,
        error,
        getQuestionSet,
        getActiveQuestions,
        handleSubmitAnswer,
        goToNext,
        goBack,
        goToQuestion,
      }}
    >
      {children}
    </QuestionContext.Provider>
  )
}
